/**
 * DOM helpers for the mobile map card carousel.
 *
 * Owns all card DOM creation for the carousel — building single cards,
 * the "see all plans" element, skeleton cards, and the card element list.
 * The plugin (carousel.plugin.js) calls these at the appropriate lifecycle
 * points but remains free of direct DOM operations.
 *
 * Effect boundary: every export touches the DOM or creates DOM nodes.
 * No business logic lives here.
 *
 * @module searchResultsMapVariant/components/carouselDom
 */

import { SeeAllPlansCard, SkeletonCard } from 'features/summaryCard';
import { getCardInstance } from '../services/cardBuilder';

/**
 * Maximum number of plan cards shown before a "see all" button is appended.
 */
export const MAX_VISIBLE_PLANS = 5;

const range = document.createRange();

/**
 * Parses an HTML string into a DocumentFragment.
 *
 * @param {string} html
 * @returns {DocumentFragment}
 */
function createHtmlFragment(html) {
  return range.createContextualFragment(html);
}

/**
 * Builds a single summary card DOM element for the given card data.
 * Returns null when no card instance is available for the type.
 *
 * @param {Object}  cardData
 * @param {boolean} isSingleCard - When true, adds full-width utility classes.
 * @returns {HTMLElement|null}
 */
export function createSingleCardElement(cardData, isSingleCard = false) {
  const { type, isBasic, isMasterPlannedCommunity } = cardData;
  const cardInstance = getCardInstance(type, isBasic, isMasterPlannedCommunity);
  if (!cardInstance) {
    return null;
  }

  cardInstance.setInformation(cardData);
  const cardElement = createHtmlFragment(cardInstance.getHtml()).querySelector('[data-map-summary-card]');
  if (!cardElement) {
    return null;
  }

  if (isSingleCard) {
    cardElement.classList.add('nhs-u-flex--0-0-full', 'nhs-u-max-width--full');
  }

  cardInstance.bindEvents?.(cardElement);
  return cardElement;
}

/**
 * Builds card DOM elements for an array of card data objects.
 *
 * @param {Object[]} cards
 * @returns {HTMLElement[]}
 */
export function createCardElements(cards) {
  const isSingleCard = cards.length === 1;
  return cards
    .map((cardData) => createSingleCardElement(cardData, isSingleCard))
    .filter(Boolean);
}

/**
 * Builds the "see all plans" element for a community that has more plans
 * than MAX_VISIBLE_PLANS.
 *
 * @param {Object} cardData - The first card's data, used for metadata.
 * @returns {HTMLElement|null}
 */
export function createSeeAllPlansElement(cardData) {
  const seeAllPlansCard = new SeeAllPlansCard();
  seeAllPlansCard.setMetadata(cardData);
  const element = createHtmlFragment(seeAllPlansCard.getHtml()).firstElementChild ?? null;
  seeAllPlansCard.bindEvents?.(element);
  return element;
}

/**
 * Builds a skeleton loading card element.
 *
 * @returns {HTMLElement|null}
 */
export function createSkeletonCardElement() {
  const skeletonCard = new SkeletonCard();
  return createHtmlFragment(skeletonCard.getHtml()).firstElementChild ?? null;
}
